import ScopeService from '../services/scopeService';
import { ALL_LOCATIONS_ID } from './locationUtils';
import { isJobviousAdmin } from './permissions';

/**
 * Marine is a PER-LOCATION capability (`installer_location.marine_enabled`),
 * NOT an installer-wide setting. Do not route this through
 * ScopeService.getCompany() — that is the wrong scope and spec §4 is stale.
 *
 * Server gap #3: no endpoint selects the column yet, so this reads undefined
 * and returns false everywhere. That is the intended dark-ship state.
 */

// CONFIRMED: the key is `marine_enabled` (not installer_-prefixed), and the
// value is a MySQL tinyint — it arrives as 1/0, NOT true/false. A `=== true`
// test silently disables marine everywhere. Coerce.
const flagOf = (loc) => {
  const v = loc?.marine_enabled ?? loc?.installer_marine_enabled;
  return v === true || v === 1 || v === '1';
};

const idOf = (loc) => String(loc?.location_id ?? loc?.id ?? '');

export function getMarineLocations() {
  return (ScopeService.getLocations() || []).filter(flagOf);
}

/**
 * Defaults to the currently-selected location. Under "All locations" this is
 * true if ANY location is marine-capable — otherwise a multi-location installer
 * viewing "All" would lose the Boat option entirely. Pinning the request to a
 * specific marine location is Task 14's job, not this function's.
 */
export function isMarineEnabled(locationId) {
  const locations = ScopeService.getLocations() || [];
  const id = locationId ?? ScopeService.getSelectedLocationId();
  if (id === ALL_LOCATIONS_ID || id == null) return locations.some(flagOf);
  return flagOf(locations.find((l) => idOf(l) === String(id)));
}

/**
 * TEMPORARY ROLLOUT GATE (added 2026-08-11) — marine is DARK-SHIPPED to
 * production for Jobvious staff to test against live data.
 *
 * `isMarineEnabled` answers "is this location marine-capable?" — a real product
 * capability that outlives this gate. This answers "should anyone see marine
 * right now?", which is a release decision and is meant to be deleted.
 *
 * Gating the ENTRY POINT is sufficient: no boat can be created without the
 * Road/Water toggle, so the area calculator and marine pricing are unreachable
 * for everyone else. Boat markers on already-existing boats still render — they
 * are read-only display over data that already exists.
 *
 * TO SHIP MARINE TO CUSTOMERS: delete this function and restore the call site
 * in vehicleSelection.jsx to `isMarineEnabled()`. Nothing else changes.
 */
export function isMarineVisible(locationId) {
  return isMarineEnabled(locationId) && isJobviousAdmin(ScopeService.getActiveScopeRole());
}
